<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const getAccount = useMapGetter('accounts/getAccount');

const clearAssignedAgent = ref(false);
const isSubmitting = ref(false);

const currentAccount = computed(() => getAccount.value(accountId.value) || {});

watch(
  currentAccount,
  account => {
    clearAssignedAgent.value =
      !!account?.settings?.clear_assigned_agent_on_resolve;
  },
  { immediate: true }
);

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch('accounts/update', {
      clear_assigned_agent_on_resolve: clearAssignedAgent.value,
    });
    useAlert(t('GENERAL_SETTINGS.RESOLVE_SETTINGS.API.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.RESOLVE_SETTINGS.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.RESOLVE_SETTINGS.TITLE')"
    :description="t('GENERAL_SETTINGS.RESOLVE_SETTINGS.NOTE')"
    with-border
  >
    <div
      class="flex flex-col outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
    >
      <div class="p-4 flex items-center justify-between gap-4">
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.RESOLVE_SETTINGS.CLEAR_AGENT.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('GENERAL_SETTINGS.RESOLVE_SETTINGS.CLEAR_AGENT.HELP') }}
          </span>
        </div>
        <Switch v-model="clearAssignedAgent" />
      </div>
    </div>
    <div class="mt-4">
      <NextButton
        blue
        :is-loading="isSubmitting"
        :label="t('GENERAL_SETTINGS.RESOLVE_SETTINGS.UPDATE_BUTTON')"
        @click="handleSubmit"
      />
    </div>
  </SectionLayout>
</template>
